/**
 * BSS Member Badge Handler
 *
 * Responsabilități (frontend theme):
 * - Pentru MEMBRI: înlocuiește badge-ul "Redus" de pe imagine cu badge access+ + % discount (compare-at vs BSS price)
 * - Pentru MEMBRI: calculează preț/kg pe card din prețul BSS (când există metafield și markup-ul price/kg)
 * - Pentru NON-MEMBRI: completează badge-ul cu preț de membru (fetch /apps/member-price) + calculează preț/kg standard
 * - Suportă conținut dinamic (Load More / AJAX) via MutationObserver
 */

(function () {
  'use strict';

  const CONFIG = {
    debug: false,
    accessBadgeImg:
      'https://cdn.shopify.com/s/files/1/0906/9415/6673/files/Group_2608683_1.png?v=1770022455',
    retryDelaysMs: [0, 150, 300, 600, 1000, 1500, 2000, 3000, 5000, 8000, 12000],
    mutationDebounceMs: 200,
    memberPriceEndpoint: '/apps/member-price',
  };

  const memberPriceCache = new Map();

  function log(...args) {
    if (!CONFIG.debug) return;
    // eslint-disable-next-line no-console
    console.log('[BSS Badge Handler]', ...args);
  }

  function parseMoneyToNumber(text) {
    if (!text) return NaN;

    // Keep digits, comma, dot.
    let normalized = String(text).replace(/[^0-9.,]/g, '');

    // If we have both separators, assume the last one is decimal separator.
    const lastComma = normalized.lastIndexOf(',');
    const lastDot = normalized.lastIndexOf('.');

    if (lastComma !== -1 && lastDot !== -1) {
      const decimalSep = lastComma > lastDot ? ',' : '.';
      const thousandSep = decimalSep === ',' ? '.' : ',';
      normalized = normalized.split(thousandSep).join('');
      normalized = normalized.replace(decimalSep, '.');
    } else {
      // Only comma present => decimal comma.
      normalized = normalized.replace(',', '.');
    }

    return parseFloat(normalized);
  }

  function formatNumber2(value) {
    if (!Number.isFinite(value)) return '';
    return value.toFixed(2);
  }

  function getProductCardRoot(node) {
    if (!node || !node.closest) return null;
    const preferred =
      node.closest(
        'product-card, .product-card, .grid-product, .grid-view-item, .product-item, .card, .card-product, .product-grid-item'
      ) || null;
    if (preferred) return preferred;

    // Fallback: some themes keep product id on an inner <a>. Avoid returning the <a>
    // because the sale badge is often outside that link.
    const dataEl = node.closest('[data-product-id]');
    if (!dataEl) return null;
    const tag = (dataEl.tagName || '').toLowerCase();
    if (tag === 'a') return dataEl.parentElement || dataEl;
    return dataEl;
  }

  function isMemberFromPriceSpan(priceSpan) {
    if (!priceSpan || !priceSpan.getAttribute) return false;
    return priceSpan.getAttribute('data-is-member') === 'true';
  }

  function buildAccessBadgeHtml(discountPercentText) {
    return (
      '<span class="access-badge">' +
      '<img src="' +
      CONFIG.accessBadgeImg +
      '" alt="access-badge">' +
      '</span> ' +
      '<span class="access-percentage">' +
      discountPercentText +
      '% </span>'
    );
  }

  function updateMemberDiscountBadgeOnCard(productCard, discountPercent, productId) {
    if (!productCard) return false;
    if (!Number.isFinite(discountPercent) || discountPercent <= 0) return false;

    const escapedProductId =
      productId && window.CSS && typeof window.CSS.escape === 'function'
        ? window.CSS.escape(String(productId))
        : productId
          ? String(productId)
          : '';

    let saleBadge =
      productCard.querySelector('.sale-badge--member-check') ||
      productCard.querySelector('.sale-badge.badge') ||
      productCard.querySelector('.f-badge--sale') ||
      productCard.querySelector('.sale-badge');

    // If badge is rendered outside the chosen card root, fall back to a product-id match.
    if (!saleBadge && escapedProductId) {
      saleBadge = document.querySelector(`.sale-badge--member-check[data-product-id="${escapedProductId}"]`);
    }

    if (!saleBadge) return false;
    if (saleBadge.classList.contains('sale-badge--member')) return true;

    const discountTextEl = saleBadge.querySelector('.badge-discount-text');
    const blackFridayEl = saleBadge.querySelector('.black-friday-badge');
    const html = buildAccessBadgeHtml(String(Math.round(discountPercent)));

    if (discountTextEl) {
      discountTextEl.innerHTML = html;
    } else {
      saleBadge.innerHTML = (blackFridayEl ? blackFridayEl.outerHTML : '') + html;
    }
    saleBadge.classList.add('sale-badge--member');
    return true;
  }

  function updateMemberPricePerKgOnCard(productCard, bssPrice) {
    if (!productCard || !Number.isFinite(bssPrice) || bssPrice <= 0) return false;

    const pricePerKgEl = productCard.querySelector('.price-per-kg[bss-b2b-price-per-kg]');
    if (!pricePerKgEl) return false;

    const cantitateKg = parseFloat(pricePerKgEl.getAttribute('data-cantitate-kg') || '');
    if (!Number.isFinite(cantitateKg) || cantitateKg <= 0) return false;

    const valueEl = pricePerKgEl.querySelector('.price-per-kg-value');
    if (!valueEl) return false;

    const pricePerKg = bssPrice / cantitateKg;
    valueEl.textContent = formatNumber2(pricePerKg) + ' lei';
    return true;
  }

  function findCompareAtElement(productCard) {
    if (!productCard || !productCard.querySelector) return null;

    const selectors = [
      // Legacy integration
      '.money.price--compare-at-price',
      // Common Shopify theme patterns
      's.price-item--regular',
      'del.price-item--regular',
      '.price__sale s',
      '.price__sale del',
      // Generic fallbacks
      '[data-compare-at-price]',
      '.price-item--regular',
      '.price__compare',
      '.compare-at-price',
      'del',
      's',
    ];

    for (const selector of selectors) {
      const el = productCard.querySelector(selector);
      if (!el) continue;

      const value = parseMoneyToNumber((el.textContent || '').trim());
      if (Number.isFinite(value) && value > 0) return el;
    }

    return null;
  }

  function processMemberCards(root) {
    const scope = root && root.querySelectorAll ? root : document;
    const spans = scope.querySelectorAll('[bss-b2b-product-id][bss-b2b-product-price]');

    spans.forEach((priceSpan) => {
      const productCard = getProductCardRoot(priceSpan);
      if (!productCard) return;

      const productId = priceSpan.getAttribute('bss-b2b-product-id') || '';
      const escapedProductId =
        productId && window.CSS && typeof window.CSS.escape === 'function'
          ? window.CSS.escape(String(productId))
          : productId
            ? String(productId)
            : '';

      const globalMemberBadge =
        escapedProductId ? document.querySelector(`.sale-badge--member-check[data-product-id="${escapedProductId}"]`) : null;

      // Membership signal:
      // - Prefer Liquid-provided data-is-member, but BSS scripts may replace nodes and drop it.
      // - For members, badge.liquid renders .sale-badge--member-check; use that as a stable signal.
      const isMemberCard =
        isMemberFromPriceSpan(priceSpan) ||
        !!productCard.querySelector('.sale-badge--member-check') ||
        !!globalMemberBadge;
      if (!isMemberCard) return;

      // Idempotency per card
      const alreadyProcessed = productCard.getAttribute('data-bss-member-processed') === 'true';
      const text = (priceSpan.textContent || '').trim();
      const bssPrice = parseMoneyToNumber(text);

      const basePriceCentsRaw = priceSpan.getAttribute('data-base-price-cents');
      const basePriceCents = basePriceCentsRaw ? parseFloat(basePriceCentsRaw) : NaN;
      const basePrice = Number.isFinite(basePriceCents) ? basePriceCents / 100 : NaN;

      if (Number.isFinite(bssPrice) && bssPrice > 0) {
        updateMemberPricePerKgOnCard(productCard, bssPrice);
      }

      // Discount badge only if we have compare-at.
      const compareAtEl = findCompareAtElement(productCard);
      let compareAtPrice = parseMoneyToNumber(compareAtEl ? compareAtEl.textContent : '');
      if (!Number.isFinite(compareAtPrice) || compareAtPrice <= 0) {
        const compareAtCentsRaw = priceSpan.getAttribute('data-compare-at-price-cents');
        const compareAtCents = compareAtCentsRaw ? parseFloat(compareAtCentsRaw) : NaN;
        if (Number.isFinite(compareAtCents) && compareAtCents > 0) {
          compareAtPrice = compareAtCents / 100;
        }
      }

      // If we can read original discount from the member-check badge, use it to avoid converting
      // before BSS replaces the price (when bssPrice still equals Liquid sale price).
      const memberCheckBadge = productCard.querySelector('.sale-badge--member-check') || globalMemberBadge;
      let originalDiscountPercent = NaN;
      if (memberCheckBadge) {
        const raw =
          memberCheckBadge.getAttribute('data-original-discount') ||
          (memberCheckBadge.querySelector('.badge-discount-text')
            ? memberCheckBadge.querySelector('.badge-discount-text').textContent
            : '');
        const parsed = parseFloat(String(raw).replace('%', '').replace(',', '.'));
        if (Number.isFinite(parsed)) originalDiscountPercent = Math.abs(parsed);
      }

      let bssLooksApplied = true;
      if (Number.isFinite(basePrice)) {
        bssLooksApplied = Number.isFinite(bssPrice) && Math.abs(bssPrice - basePrice) > 0.001;
      } else if (Number.isFinite(originalDiscountPercent) && originalDiscountPercent > 0) {
        const computedDiscount =
          Number.isFinite(bssPrice) && bssPrice > 0 && Number.isFinite(compareAtPrice) && compareAtPrice > 0
            ? ((compareAtPrice - bssPrice) / compareAtPrice) * 100
            : NaN;
        // Consider applied only when computed discount differs from original by at least 1pp.
        bssLooksApplied =
          Number.isFinite(computedDiscount) && Math.abs(computedDiscount - originalDiscountPercent) >= 1;
      }

      if (
        !alreadyProcessed &&
        bssLooksApplied &&
        Number.isFinite(bssPrice) &&
        bssPrice > 0 &&
        Number.isFinite(compareAtPrice) &&
        compareAtPrice > 0
      ) {
        const discountPercent = ((compareAtPrice - bssPrice) / compareAtPrice) * 100;
        if (discountPercent > 0) {
          const updated = updateMemberDiscountBadgeOnCard(productCard, discountPercent, productId);
          if (updated) {
            productCard.setAttribute('data-bss-member-processed', 'true');
            log('Member badge updated', priceSpan.getAttribute('bss-b2b-product-id'), Math.round(discountPercent) + '%');
          }
        }
      }
    });
  }

  function processNonMemberPricePerKg(root) {
    const scope = root && root.querySelectorAll ? root : document;
    const wrappers = scope.querySelectorAll('.price-per-kg-non-member-wrapper:not([data-nm-kg-updated])');

    wrappers.forEach((wrapper) => {
      const priceCents = parseFloat(wrapper.getAttribute('data-price-cents') || '');
      const cantitateKg = parseFloat(wrapper.getAttribute('data-cantitate-kg') || '');
      const valueEl = wrapper.querySelector('.js-non-member-price-per-kg-value');

      if (!valueEl) return;
      if (!Number.isFinite(priceCents) || !Number.isFinite(cantitateKg) || cantitateKg <= 0) return;

      const priceLei = priceCents / 100;
      const perKg = priceLei / cantitateKg;
      valueEl.textContent = formatNumber2(perKg);
      wrapper.setAttribute('data-nm-kg-updated', 'true');
    });
  }

  async function fetchMemberPrice(productId, variantId) {
    const key = `${productId}:${variantId}`;
    if (memberPriceCache.has(key)) return memberPriceCache.get(key);

    const url = `${CONFIG.memberPriceEndpoint}?product_id=${encodeURIComponent(productId)}&variant_id=${encodeURIComponent(variantId)}`;

    const promise = fetch(url)
      .then((r) => (r.ok ? r.json() : null))
      .catch(() => null);

    memberPriceCache.set(key, promise);
    return promise;
  }

  function processNonMemberMemberPriceBadges(root) {
    const scope = root && root.querySelectorAll ? root : document;

    const badges = scope.querySelectorAll('.member-price-badge-non-member:not([data-mp-updated])');
    badges.forEach((badge) => {
      const productId = badge.getAttribute('data-card-product-id') || badge.getAttribute('data-product-id');
      const variantId = badge.getAttribute('data-card-variant-id') || badge.getAttribute('data-variant-id');
      const valueEl = badge.querySelector('.js-member-price-value');

      if (!productId || !variantId || !valueEl) return;
      badge.setAttribute('data-mp-updated', 'true');

      fetchMemberPrice(productId, variantId).then((data) => {
        if (!data || !data.ok || !data.has_member_pricing || !data.member_price) return;
        const memberPrice = parseFloat(data.member_price);
        if (!Number.isFinite(memberPrice) || memberPrice <= 0) return;
        valueEl.textContent = formatNumber2(memberPrice);
        badge.style.display = 'flex';
      });
    });
  }

  function findProductMediaRoots() {
    const selectors = [
      '.product__media-wrapper',
      '.product-gallery',
      '.product__media',
      '.product-images',
      '[data-product-single-media-wrapper]',
      '.media-gallery',
      '.product-media',
      '.product__media-list',
    ];

    const roots = [];
    selectors.forEach((sel) => {
      document.querySelectorAll(sel).forEach((el) => {
        if (el && !roots.includes(el)) roots.push(el);
      });
    });
    return roots;
  }

  function findPrimaryProductIdFromDom() {
    const span = document.querySelector('.price--bss-price[bss-b2b-product-id]');
    const pid = span && span.getAttribute('bss-b2b-product-id');
    if (!pid) return null;
    return String(pid);
  }

  function updateProductPageBadge(productId) {
    const escapedProductId =
      window.CSS && typeof window.CSS.escape === 'function'
        ? window.CSS.escape(String(productId))
        : String(productId);

    // Member page price span exists only for members in template--product.liquid
    const mainPriceSpan = document.querySelector(
      `.price--bss-price[bss-b2b-product-id="${escapedProductId}"]`
    );
    if (!mainPriceSpan) return false;

    const bssPrice = parseMoneyToNumber((mainPriceSpan.textContent || '').trim());
    if (!Number.isFinite(bssPrice) || bssPrice <= 0) return false;

    const basePriceCentsRaw = mainPriceSpan.getAttribute('data-base-price-cents');
    const basePriceCents = basePriceCentsRaw ? parseFloat(basePriceCentsRaw) : NaN;
    const basePrice = Number.isFinite(basePriceCents) ? basePriceCents / 100 : NaN;

    const priceGroup =
      mainPriceSpan.closest('.product-price-group') ||
      mainPriceSpan.closest('[data-updates-on-change]') ||
      mainPriceSpan.parentElement ||
      document;

    const compareAtEl =
      priceGroup.querySelector('.price-info--compare .money') ||
      document.querySelector('.price-info--compare .money');
    let compareAtPrice = parseMoneyToNumber(compareAtEl ? compareAtEl.textContent : '');
    if (!Number.isFinite(compareAtPrice) || compareAtPrice <= 0) {
      const compareAtCentsRaw = mainPriceSpan.getAttribute('data-compare-at-price-cents');
      const compareAtCents = compareAtCentsRaw ? parseFloat(compareAtCentsRaw) : NaN;
      if (Number.isFinite(compareAtCents) && compareAtCents > 0) {
        compareAtPrice = compareAtCents / 100;
      }
    }
    if (!Number.isFinite(compareAtPrice) || compareAtPrice <= 0) {
      // Fallback for products without compare_at_price (member pricing only).
      if (Number.isFinite(basePrice) && basePrice > 0) {
        compareAtPrice = basePrice;
      }
    }
    if (!Number.isFinite(compareAtPrice) || compareAtPrice <= 0) return false;

    // Show the hidden compare line only when BSS actually applied a different price.
    if (Number.isFinite(basePrice) && basePrice > 0 && Math.abs(bssPrice - basePrice) > 0.001) {
      const hiddenCompareLine =
        priceGroup.querySelector('[data-bss-compare-line]') || document.querySelector('[data-bss-compare-line]');
      if (hiddenCompareLine && hiddenCompareLine.style && hiddenCompareLine.style.display === 'none') {
        hiddenCompareLine.style.display = '';
      }
    }

    const discountPercent = ((compareAtPrice - bssPrice) / compareAtPrice) * 100;
    if (!Number.isFinite(discountPercent) || discountPercent <= 0) return false;

    const mediaRoots = findProductMediaRoots();
    const badgeSelector = '.sale-badge--member-check, .sale-badge.badge, .sale-badge, .f-badge--sale';
    /** @type {Element[]} */
    const badges = [];

    // Best signal for members: badge.liquid sets data-product-id on .sale-badge--member-check
    // so we can match the current product reliably (works even if badge is outside media roots).
    document
      .querySelectorAll(`.sale-badge--member-check[data-product-id="${escapedProductId}"]`)
      .forEach((el) => badges.push(el));

    function isDiscountLikeBadge(el) {
      if (!el) return false;
      if (el.matches('.sale-badge--member-check, .sale-badge.badge')) return true;
      if (el.matches('.f-badge--sale')) return true;
      return !!el.querySelector('.badge-discount-text');
    }

    mediaRoots.forEach((root) => {
      root.querySelectorAll(badgeSelector).forEach((el) => {
        if (isDiscountLikeBadge(el)) badges.push(el);
      });
    });

    if (!badges.length) {
      // Fallback: try to find a badge that belongs to the main product area.
      document.querySelectorAll(badgeSelector).forEach((el) => {
        if (!isDiscountLikeBadge(el)) return;
        if (el.closest('.product__media-wrapper, .product__media, .product-gallery, .media-gallery')) {
          badges.push(el);
        }
      });
    }

    if (!badges.length) return false;

    const html = buildAccessBadgeHtml(String(Math.round(discountPercent)));

    badges.forEach((saleBadge) => {
      if (!saleBadge || saleBadge.classList.contains('sale-badge--member')) return;

      const discountTextEl = saleBadge.querySelector('.badge-discount-text');
      const blackFridayEl = saleBadge.querySelector('.black-friday-badge');

      if (discountTextEl) {
        discountTextEl.innerHTML = html;
      } else {
        saleBadge.innerHTML = (blackFridayEl ? blackFridayEl.outerHTML : '') + html;
      }
      saleBadge.classList.add('sale-badge--member');
    });

    log('Product page badge updated', productId, Math.round(discountPercent) + '%');
    return true;
  }

  function maybeUpdateProductPageBadgeAuto() {
    const productId = findPrimaryProductIdFromDom();
    if (!productId) return false;
    return updateProductPageBadge(productId);
  }

  function processAll(root) {
    processMemberCards(root);
    processNonMemberMemberPriceBadges(root);
    processNonMemberPricePerKg(root);

    // Product page (especially mobile) may not execute inline init scripts after section re-render.
    // Attempt an auto-update when a member price span is present.
    maybeUpdateProductPageBadgeAuto();
  }

  function scheduleRetries() {
    CONFIG.retryDelaysMs.forEach((delay) => {
      setTimeout(() => processAll(document), delay);
    });
  }

  function setupDynamicObserver() {
    let timer = null;

    const observer = new MutationObserver((mutations) => {
      let relevant = false;

      for (const mutation of mutations) {
        for (const node of mutation.addedNodes) {
          if (node.nodeType !== 1) continue;
          const el = /** @type {Element} */ (node);
          if (
            el.matches?.('[data-product-id], [bss-b2b-product-id], .member-price-badge-non-member, .price-per-kg-non-member-wrapper') ||
            el.querySelector?.('[data-product-id], [bss-b2b-product-id], .member-price-badge-non-member, .price-per-kg-non-member-wrapper')
          ) {
            relevant = true;
            break;
          }
        }
        if (relevant) break;
      }

      if (!relevant) return;
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => {
        processAll(document);
        scheduleRetries();
      }, CONFIG.mutationDebounceMs);
    });

    observer.observe(document.body, { childList: true, subtree: true });
  }

  function init() {
    log('init');
    processAll(document);
    scheduleRetries();
    setupDynamicObserver();
  }

  function initProductPage(productId) {
    // Also process cards (featured/recommendations)
    processAll(document);
    CONFIG.retryDelaysMs.forEach((delay) => {
      setTimeout(() => {
        updateProductPageBadge(productId);
        processAll(document);
      }, delay);
    });
  }

  window.BSSMemberBadgeHandler = {
    init,
    processAllProductCards: () => processAll(document),
    initProductPage,
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
